import { ApolloQueryResult, gql } from "@apollo/client";
import { BookCard } from "components/book-card";
import { Button } from "components/buttons/button";
import { EventCard } from "components/event-card";
import { inputClassNames } from "components/forms/input";
import { Layout } from "components/layout";
import { PostCard } from "components/post-card";
import { ProductCard } from "components/product-card";
import { PifAndVoucherCards } from "components/shop/pif-voucher-cards";
import { SubCard } from "components/sub-card";
import {
  BookCardDataFragmentDoc,
  EventCardDataFragmentDoc,
  LogNoResultSearchMutationVariables,
  PostCardDataFragmentDoc,
  ProductCardDataFragmentDoc,
  SiteSearchQueryResult,
  SiteSearchQueryVariables,
  SubCardDataFragmentDoc,
  useSiteSearchQuery,
} from "lib/graphql-generated";
import { addApolloState, initializeApollo } from "lib/hooks/apollo-client";
import { useInput } from "lib/hooks/use-input";
import { parseParam } from "lib/utils";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import querystring from "querystring";
import { logger } from "server/logger";

const SITE_SEARCH = gql`
  query SiteSearch(
    $search: String!
    $bookLimit: Int!
    $postLimit: Int!
    $eventLimit: Int!
    $productLimit: Int!
    $subLimit: Int!
    $offset: Int!
  ) {
    search_books_aggregate(args: { search: $search }) {
      aggregate {
        count
      }
    }
    search_books(
      args: { search: $search }
      where: { _and: [{ is_unlisted: { _eq: false } }] }
      limit: $bookLimit
      offset: $offset
    ) {
      ...BookCardData
    }
    search_posts_aggregate(args: { search: $search }) {
      aggregate {
        count
      }
    }
    search_posts(
      args: { search: $search }
      limit: $postLimit
      offset: $offset
    ) {
      ...PostCardData
    }
    search_events_aggregate(args: { search: $search }) {
      aggregate {
        count
      }
    }
    search_events(
      args: { search: $search }
      limit: $eventLimit
      offset: $offset
    ) {
      ...EventCardData
    }
    search_products_aggregate(
      args: { search: $search }
      where: { is_deleted: { _eq: false } }
    ) {
      aggregate {
        count
      }
    }
    search_products(
      args: { search: $search }
      where: { is_deleted: { _eq: false } }
      limit: $productLimit
      offset: $offset
    ) {
      ...ProductCardData
    }
    search_subscriptions_aggregate(
      args: { search: $search }
      where: { is_active: { _eq: true } }
      limit: $subLimit
      offset: $offset
    ) {
      aggregate {
        count
      }
    }

    search_subscriptions(
      args: { search: $search }
      where: { is_active: { _eq: true } }
      limit: $subLimit
      offset: $offset
    ) {
      ...SubCardData
    }
  }
  ${BookCardDataFragmentDoc}
  ${PostCardDataFragmentDoc}
  ${EventCardDataFragmentDoc}
  ${ProductCardDataFragmentDoc}
  ${SubCardDataFragmentDoc}
`;

const LOG_NO_RESULT_SEARCH = gql`
  mutation LogNoResultSearch($text: String!) {
    insert_empty_searches_one(object: { string: $text }) {
      id
    }
  }
`;

interface Props {
  queryVars: SiteSearchQueryVariables;
}

type SearchType = "books" | "events" | "posts" | "products" | "subscriptions";
const generalLimit = 20;
const expandedLimit = 40;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = initializeApollo();
  let queryVars: SiteSearchQueryVariables = null;
  let p = parseInt(parseParam(ctx.query.p ?? "0"));
  let type: SearchType = parseParam(ctx.query.type) as SearchType;
  const limit = !!type ? expandedLimit : generalLimit;
  const searchQuery = parseParam(ctx.query.q);
  const offset = p * limit;

  if (!!ctx.query["q"]) {
    queryVars = {
      search: searchQuery,
      bookLimit: type === "books" || !type ? limit : 0,
      eventLimit: type === "events" || !type ? limit : 0,
      postLimit: type === "posts" ? limit : !type ? 3 : 0,
      productLimit: type === "products" || !type ? limit : 0,
      subLimit: type === "subscriptions" || !type ? limit : 0,
      offset: offset,
    };

    const { data }: ApolloQueryResult<SiteSearchQueryResult["data"]> =
      await client.query({
        query: SITE_SEARCH,
        variables: queryVars,
      });

    if (
      (type === "books" || !type) &&
      data.search_books_aggregate.aggregate.count === 0
    ) {
      const insertNoOpSearch = await client.mutate({
        mutation: LOG_NO_RESULT_SEARCH,
        variables: {
          text: searchQuery,
        } as LogNoResultSearchMutationVariables,
      });
    }
  }

  logger.debug(queryVars);

  return addApolloState(client, {
    props: {
      queryVars: queryVars,
    } as Props,
  });
};

export const SearchPage = ({ queryVars }: Props) => {
  const router = useRouter();
  const type = parseParam(router.query.type) as SearchType;
  const { data } = useSiteSearchQuery({
    variables: queryVars,
  });
  const search = useInput<string>("");

  const handleSearchMore = (type: SearchType) => {
    const path = router.pathname;
    const query = querystring.encode({
      q: queryVars.search,
      p: 0,
      type: type,
    });

    router.push(`${path}?${query}`);
  };

  const getQueryString = (page: number): string => {
    return querystring.encode({
      q: queryVars.search,
      p: page,
      type: type,
    });
  };

  const noBooks = data?.search_books_aggregate.aggregate.count < 1;
  const noEvents = data?.search_events_aggregate.aggregate.count < 1;
  const noProducts = data?.search_products_aggregate.aggregate.count < 1;
  const noPosts = data?.search_posts_aggregate.aggregate.count < 1;
  const noSubs = data?.search_subscriptions_aggregate.aggregate.count < 1;
  const anyNoRes = noBooks || noEvents || noProducts || noPosts || noSubs;

  if (!queryVars) {
    return (
      <Layout pageName='Search'>
        <article className='max-w-screen-2xl min-h-96 py-16 flex flex-col mx-auto bg-green-default header justify-center'>
          <header className='text-white text-center text-4xl  w-full mx-auto'>
            Search
          </header>
          <form
            onSubmit={() => router.push(`/search?q=${search.value}`)}
            className='px-4 flex flex-col space-y-4'>
            <input
              {...search.bind}
              className={`${inputClassNames} text-center`}
              placeholder='Search for books'
            />
            <Button
              buttonType='primary'
              className='mx-auto'
              onClick={() => router.push(`/search?q=${search.value}`)}>
              Search
            </Button>
          </form>
        </article>
      </Layout>
    );
  }

  return (
    <>
      <Layout pageName='Search'>
        <article className=''>
          <header className='bg-green-default'>
            <section className='max-w-screen-2xl mx-auto text-white pt-8 pb-6 px-4 2xl:px-0 flex flex-col'>
              <h1 className='header text-5xl md:text-7xl text-center'>
                {type === "books"
                  ? "Books "
                  : type === "posts"
                  ? "Posts "
                  : type === "events"
                  ? "Events "
                  : type === "products"
                  ? "Products "
                  : ""}
                Search Results
              </h1>
              <p className='font-header text-2xl text-center w-full'>
                for: <span className='underline'>{queryVars.search}</span>
              </p>
            </section>
          </header>
          {anyNoRes ? (
            <section className='w-full max-w-screen-2xl mx-auto py-8 px-4 2xl:px-0 flex'>
              No Results for: {noBooks ? "Books, " : null}
              {noEvents ? "Events, " : null} {noProducts ? "Products, " : null}
              {noPosts ? "Posts " : null} {noSubs ? "Subscriptions" : null}
            </section>
          ) : null}
          {!noPosts && (!type || type === "posts") && (
            <section className='w-full max-w-screen-2xl mx-auto py-8 px-4 2xl:px-0'>
              <h2 className='header text-4xl pb-8'>Posts</h2>
              {data?.search_posts.length > 0 ? (
                <ul className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-8 lg:gap-16'>
                  {data?.search_posts.map((p) => (
                    <PostCard post={p} className='' key={p.id} />
                  ))}
                </ul>
              ) : (
                <p className=''>No Posts found for this search</p>
              )}
              {!type &&
                data.search_posts_aggregate.aggregate.count >
                  queryVars.postLimit && (
                  <Button
                    buttonType='secondary'
                    buttonSize='lg'
                    className='col-span-full mt-8'
                    type='button'
                    onClick={() => handleSearchMore("posts")}>
                    view more posts...
                  </Button>
                )}
            </section>
          )}
          {!noBooks && (!type || type === "books") && (
            <section className='w-full max-w-screen-2xl mx-auto py-8 px-4 2xl:px-0 '>
              <h2 className='header text-4xl pb-8'>Books</h2>
              {data?.search_books.length > 0 ? (
                <ul className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-8 lg:gap-16'>
                  {data?.search_books.map((b) => (
                    <BookCard book={b} key={b.isbn} />
                  ))}
                </ul>
              ) : (
                <p className=''>No Books found for this search</p>
              )}
              {!type &&
                data.search_books_aggregate.aggregate.count >
                  queryVars.bookLimit && (
                  <Button
                    buttonType='secondary'
                    buttonSize='lg'
                    className='col-span-full mt-8'
                    type='button'
                    onClick={() => handleSearchMore("books")}>
                    view more books...
                  </Button>
                )}
            </section>
          )}
          {!noEvents && (!type || type === "events") && (
            <section className='w-full max-w-screen-2xl mx-auto py-8 px-4 2xl:px-0'>
              <h2 className='header text-4xl pb-8'>Events</h2>
              {data?.search_events.length > 0 ? (
                <ul className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3  gap-4 md:gap-8 lg:gap-16'>
                  {data?.search_events.map((e) => (
                    <EventCard event={e} className='' key={e.id} />
                  ))}
                </ul>
              ) : (
                <p className=''>No Events found for this search</p>
              )}
              {!type &&
                data.search_events_aggregate.aggregate.count >
                  queryVars.eventLimit && (
                  <Button
                    buttonType='secondary'
                    buttonSize='lg'
                    className='col-span-full mt-8'
                    type='button'
                    onClick={() => handleSearchMore("events")}>
                    view more events...
                  </Button>
                )}
            </section>
          )}
          {!noSubs && (!type || type === "subscriptions") && (
            <section className='w-full max-w-screen-2xl mx-auto py-8 px-4 2xl:px-0'>
              <h2 className='header text-4xl pb-8'>Subscriptions</h2>
              {data?.search_posts.length > 0 ? (
                <ul className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-8 lg:gap-16'>
                  {data?.search_subscriptions.map((s) => (
                    <SubCard sub={s} className='' key={s.id} />
                  ))}
                </ul>
              ) : (
                <p className=''>No Subscriptions found for this search</p>
              )}
              {!type &&
                data.search_subscriptions_aggregate.aggregate.count >
                  queryVars.postLimit && (
                  <Button
                    buttonType='secondary'
                    buttonSize='lg'
                    className='col-span-full mt-8'
                    type='button'
                    onClick={() => handleSearchMore("posts")}>
                    view more subscriptions...
                  </Button>
                )}
            </section>
          )}

          {!noProducts && (!type || type === "products") && (
            <section className='w-full max-w-screen-2xl mx-auto py-8 px-4 2xl:px-0'>
              <h2 className='header text-4xl pb-8'>Merch</h2>
              {data?.search_products.length > 0 ? (
                <ul className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-8 lg:gap-16'>
                  <PifAndVoucherCards className='mx-auto' />
                  {data?.search_products.map((p) => (
                    <ProductCard product={p} className='mx-auto' key={p.id} />
                  ))}
                </ul>
              ) : (
                <p className=''>No Merch found for this search</p>
              )}
              {!type &&
                data.search_products_aggregate.aggregate.count >
                  queryVars.postLimit && (
                  <Button
                    buttonType='secondary'
                    buttonSize='lg'
                    className='col-span-full mt-8'
                    type='button'
                    onClick={() => handleSearchMore("posts")}>
                    view more merch...
                  </Button>
                )}
            </section>
          )}
          {data && (
            <nav
              className='py-3 flex items-center justify-between border-t border-gray-200 max-w-screen-2xl mx-auto'
              aria-label='Pagination'>
              <div className='hidden sm:block'>
                <p className='text-sm text-gray-700'>
                  Showing{" "}
                  <span className='font-medium'>
                    {queryVars.offset}
                    {` `}
                  </span>
                  to{" "}
                  <span className='font-medium'>
                    {Math.min(
                      queryVars.offset + expandedLimit,
                      type === "books"
                        ? data.search_books_aggregate.aggregate.count
                        : type === "events"
                        ? data.search_events_aggregate.aggregate.count
                        : type === "posts"
                        ? data.search_posts_aggregate.aggregate.count
                        : data.search_products_aggregate.aggregate.count + 2
                    )}
                    {` `}
                  </span>
                  of{" "}
                  <span className='font-medium'>
                    {type === "books"
                      ? data.search_books_aggregate.aggregate.count
                      : type === "events"
                      ? data.search_events_aggregate.aggregate.count
                      : type === "posts"
                      ? data.search_posts_aggregate.aggregate.count
                      : data.search_products_aggregate.aggregate.count + 2}
                    {` `}
                  </span>
                  results
                </p>
              </div>
              <div className='flex-1 flex justify-between space-x-2 sm:justify-end'>
                {queryVars.offset !== 0 && (
                  <Button
                    buttonType='ghost'
                    buttonSize='lg'
                    href={`/search?${getQueryString(
                      queryVars.offset / expandedLimit - 1
                    )}`}>
                    previous
                  </Button>
                )}
                {queryVars.offset / expandedLimit !==
                  Math.floor(
                    type === "books"
                      ? data.search_books_aggregate.aggregate.count /
                          expandedLimit
                      : type === "events"
                      ? data.search_events_aggregate.aggregate.count /
                        expandedLimit
                      : type === "posts"
                      ? data.search_posts_aggregate.aggregate.count /
                        expandedLimit
                      : data.search_products_aggregate.aggregate.count +
                        2 / expandedLimit
                  ) && (
                  <Button
                    buttonType='ghost'
                    buttonSize='lg'
                    href={`/search?${getQueryString(
                      queryVars.offset / expandedLimit + 1
                    )}`}>
                    Next
                  </Button>
                )}
              </div>
            </nav>
          )}
        </article>
      </Layout>
    </>
  );
};

export default SearchPage;
